import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import mediaService from '../../services/mediaService';
import championshipService from '../../services/championshipService';
import useAuth from '../../hooks/useAuth';
import './ChampionshipMediaTab.css';

function ChampionshipMediaTab(props) {
  const routeParams = useParams();
  const championshipId = props.championshipId || routeParams.id || routeParams.championshipId;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [championship, setChampionship] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filesToUpload, setFilesToUpload] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const fetchChampionship = useCallback(async () => {
    try {
      const response = await championshipService.getById(championshipId);
      if (response.data) {
        setChampionship(response.data);
      } else {
        throw new Error('Campeonato não encontrado');
      }
    } catch (err) {
      console.error('Erro ao carregar campeonato:', err);
      setError('Erro ao carregar informações do campeonato');
      navigate('/championships');
    }
  }, [championshipId, navigate]);

  const fetchMedia = useCallback(async () => {
    try {
      setLoading(true);
      const { success, data, error } = await mediaService.getMediaForChampionship(championshipId);
      if (success) {
        setMedia(data);
        setError('');
      } else {
        setError(error);
      }
    } catch (err) {
      setError('Erro inesperado ao carregar mídias');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [championshipId]);

  useEffect(() => {
    if (!championshipId) {
      navigate('/championships');
      return;
    }

    fetchChampionship();
    fetchMedia();
  }, [championshipId, fetchChampionship, fetchMedia, navigate]);

  // 🔹 Verifica se usuário é criador, gestor do campeonato ou admin
  const canManageMedia = useMemo(() => {
    if (!user || !championship) return false;
    
    // Admin sempre pode gerenciar
    if (user.role && user.role.toLowerCase() === 'admin') return true;
    
    const userId = user._id;
    const isCreator = championship.creator?._id === userId;
    const isManager = championship.managers?.some(m => m._id === userId);
    return isCreator || isManager;
  }, [user, championship]);

  const handleFileChange = (e) => {
    setFilesToUpload(Array.from(e.target.files));
    setUploadError('');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (filesToUpload.length === 0) {
      setUploadError('Selecione pelo menos um arquivo');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    const { success, error } = await mediaService.uploadMedia(
      championshipId,
      filesToUpload,
      title,
      description
    );

    if (success) {
      setShowForm(false);
      setFilesToUpload([]);
      setTitle('');
      setDescription('');
      await fetchMedia();
    } else {
      setUploadError(error);
    }

    setIsUploading(false);
  };

  const handleDelete = async (mediaId) => {
    if (window.confirm('Tem certeza que deseja apagar esta mídia?')) {
      const { success } = await mediaService.deleteMedia(mediaId);
      if (success) {
        setMedia(prev => prev.filter(m => m._id !== mediaId));
      }
    }
  };

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error('Falha ao baixar arquivo');

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename || 'arquivo';
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      alert('Erro ao baixar arquivo: ' + error.message);
    }
  };

  if (!championship) {
    return (
      <div className="loading-container">
        <p>Carregando informações do campeonato...</p>
      </div>
    );
  }

  return (
    <div className="media-tab-container">
      <div className="media-header">
        <h2>Galeria do Campeonato: {championship.name}</h2>
        {canManageMedia && (
          <button 
            onClick={() => setShowForm(!showForm)} 
            className={`toggle-form-button ${showForm ? 'cancel' : 'add'}`}
          >
            {showForm ? 'Cancelar Upload' : 'Adicionar Mídia'}
          </button>
        )}
      </div>

      {showForm && canManageMedia && (
        <div className="upload-form-container">
          <form onSubmit={handleUpload}>
            <div className="form-group">
              <label>Arquivos (Fotos ou Vídeos)*</label>
              <input 
                type="file" 
                multiple 
                onChange={handleFileChange} 
                accept="image/*,video/*" 
                required 
              />
              <small>Formatos suportados: JPG, PNG, MP4 (Máx. 10MB cada)</small>
            </div>
            <div className="form-group">
              <label>Título</label>
              <input 
                type="text" 
                value={title} 
                onChange={e => setTitle(e.target.value)} 
                placeholder="Ex: Melhores momentos - Final" 
              />
            </div>
            <div className="form-group">
              <label>Descrição</label>
              <textarea 
                value={description} 
                onChange={e => setDescription(e.target.value)} 
                placeholder="Descrição sobre la mídia"
                rows="3"
              ></textarea>
            </div>
            {uploadError && <p className="error-text">{uploadError}</p>}
            <button 
              type="submit" 
              disabled={isUploading}
              className="submit-button"
            >
              {isUploading ? (
                <>
                  <span className="spinner"></span>
                  Enviando...
                </>
              ) : 'Enviar Mídia'}
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <p>Carregando mídias...</p>
        </div>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : media.length === 0 ? (
        <div className="empty-state">
          <p>Nenhuma mídia encontrada para este campeonato.</p>
          {canManageMedia && (
            <button 
              onClick={() => setShowForm(true)}
              className="add-media-button"
            >
              Adicionar Primeira Mídia
            </button>
          )}
        </div>
      ) : (
        <div className="media-grid">
          {media.map(item => (
            <div key={item._id} className="media-item">
              {item.mediaType === 'image' ? (
                <img 
                  src={mediaService.getMediaUrl(item)} 
                  alt={item.title || 'Imagem do campeonato'} 
                  className="media-content"
                  loading="lazy"
                />
              ) : (
                <div className="video-container">
                  <video controls className="media-content">
                    <source src={mediaService.getMediaUrl(item)} type={item.mimetype} />
                    Seu navegador não suporta vídeos.
                  </video>
                </div>
              )}
              <div className="media-info">
                {item.title && <h4>{item.title}</h4>}
                {item.description && <p>{item.description}</p>}
                <small>
                  Enviado por: {item.uploader?.name || 'Usuário desconhecido'} • 
                  {new Date(item.createdAt).toLocaleDateString('pt-BR')}
                </small>

                {/* Botão de download - qualquer usuário pode baixar */}
                <button
                  onClick={() => handleDownload(mediaService.getMediaUrl(item), item.title || 'mídia')}
                  className="download-media-button"
                  title="Baixar mídia"
                >
                  Baixar
                </button>

                {/* Botão de exclusão - só criador/gestores/admin */}
                {canManageMedia && (
                  <button 
                    onClick={() => handleDelete(item._id)} 
                    className="delete-media-button"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ChampionshipMediaTab;